import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';
import { RootState } from './store';

// ✅ Interface Payment
interface Payment {
  id: number;
  amount: number;
  date: string;
  LeaseId?: number;
  payment_type?: string;
  rent_amount?: number;
  mode?: string;
  [key: string]: any;
}

// ✅ Interface State
interface PaymentsState {
  payments: Payment[];
  payment: Payment | null;
  loading: boolean;
  error: string | null;
}

// ✅ Initial State
const initialState: PaymentsState = {
  payments: [],
  payment: null,
  loading: false,
  error: null,
};

// ========================================
// ASYNC THUNKS
// ========================================

// ✅ Fetch all payments
export const fetchPayments = createAsyncThunk<Payment[], void, { state: RootState }>(
  'payments/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      
      // ✅ CORRECTION : /rentpayments au lieu de /payments/all
      const response = await axios.get(`${baseUrlApi}/rentpayments`, {
        headers: { Authorization: `bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments');
    }
  }
);

// ✅ Fetch single payment by LeaseId (BailId)
export const fetchPayment = createAsyncThunk<Payment, number, { state: RootState }>(
  'payments/fetchOne',
  async (leaseId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      
      // ✅ CORRECTION : /rentpayments/${leaseId}
      const response = await axios.get(`${baseUrlApi}/rentpayments/${leaseId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchPayments());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment');
    }
  }
);

// ✅ Create payment (anciennement createPayments)
export const createPayment = createAsyncThunk<Payment, Partial<Payment>, { state: RootState }>(
  'payments/create',
  async (paymentData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      
      // ✅ CORRECTION : /rentpayments
      const response = await axios.post(`${baseUrlApi}/rentpayments`, paymentData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchPayments());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "L'ajout du Paiement a échoué !");
    }
  }
);

// ✅ Update payment
export const updatePayment = createAsyncThunk<
  Payment,
  { paymentData: Partial<Payment>; paymentId: number },
  { state: RootState }
>(
  'payments/update',
  async ({ paymentData, paymentId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      
      // ✅ CORRECTION : /rentpayments/${paymentId}
      const response = await axios.put(`${baseUrlApi}/rentpayments/${paymentId}`, paymentData, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchPayments());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update payment');
    }
  }
);

// ✅ Delete payment
export const deletePayment = createAsyncThunk<number, number, { state: RootState }>(
  'payments/delete',
  async (paymentId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const token = authentification.user.token;
      
      // ✅ CORRECTION : /rentpayments/${paymentId}
      await axios.delete(`${baseUrlApi}/rentpayments/${paymentId}`, {
        headers: { Authorization: `bearer ${token}` }
      });
      
      dispatch(fetchPayments());
      return paymentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'La suppression du versement a échouée !');
    }
  }
);

// ========================================
// SLICE
// ========================================

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearPayment: (state) => {
      state.payment = null;
    },
    clearPayments: (state) => {
      state.payments = [];
    },
    resetPaymentsState: () => initialState,
  },
  extraReducers: (builder) => {
    // ✅ Handle pending and rejected states for all thunks
    [fetchPayments, fetchPayment, createPayment, updatePayment, deletePayment].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    // ✅ Handle fulfilled states
    builder
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payment = action.payload;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments.push(action.payload);
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.payments.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.payments[index] = action.payload;
        }
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = state.payments.filter(p => p.id !== action.payload);
      });
  },
});

// ✅ Export actions
export const { clearPayment, clearPayments, resetPaymentsState } = paymentsSlice.actions;

// ✅ Export reducer
export default paymentsSlice.reducer;